/**
 * VaultGrid - Center pane for displaying collection items
 * USER-CON-001 Phase 2
 *
 * OPT-006: Uses react-window for virtualized list rendering (10k+ items)
 * OPT-007: ARIA attributes for accessibility (WCAG 2.2 AA)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';
import {
  Grid, List, Search, Filter, Package, ChevronDown, RefreshCw,
  Check, Square, CheckSquare, DollarSign, Box, Trash2
} from 'lucide-react';
import AutoGradeStatusBadge from './AutoGradeStatusBadge';

// Collection item card component with ARIA
function VaultItemCard({ item, isSelected, isActive, onClick, onSelect, onDoubleClick, index }) {
  const gradeColor = item.is_graded
    ? 'text-green-400'
    : item.user_grade
    ? 'text-yellow-400'
    : 'text-zinc-500';

  return (
    <article
      role="listitem"
      aria-selected={isActive}
      aria-label={`${item.title || 'Untitled'} Issue ${item.issue_number || 'unknown'}`}
      tabIndex={0}
      onClick={() => onClick(item)}
      onDoubleClick={() => onDoubleClick?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(item);
        }
      }}
      className={`bg-zinc-900 border rounded-lg overflow-hidden transition-all cursor-pointer group relative ${
        isActive
          ? 'border-orange-500 ring-2 ring-orange-500/20'
          : isSelected
          ? 'border-orange-500/50'
          : 'border-zinc-800 hover:border-zinc-700'
      }`}
    >
      {/* Selection Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(item);
        }}
        aria-label={isSelected ? 'Deselect item' : 'Select item'}
        aria-pressed={isSelected}
        className={`absolute top-2 left-2 z-10 p-1 rounded transition-all ${
          isSelected
            ? 'bg-orange-500 text-white'
            : 'bg-zinc-900/80 text-zinc-400 opacity-0 group-hover:opacity-100 focus:opacity-100'
        }`}
      >
        {isSelected ? (
          <CheckSquare className="w-4 h-4" aria-hidden="true" />
        ) : (
          <Square className="w-4 h-4" aria-hidden="true" />
        )}
      </button>

      {/* Cover Image */}
      <div className="aspect-[2/3] bg-zinc-800 relative">
        {item.cover_photo_url ? (
          <img
            src={item.cover_photo_url}
            alt={`Cover of ${item.title}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
            <Package className="w-12 h-12 text-zinc-700" />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1" role="group" aria-label="Item badges">
          {item.is_graded && (
            <span className="px-1.5 py-0.5 bg-green-500/90 text-white text-[10px] font-bold rounded uppercase">
              {item.grading_company?.toUpperCase()}
            </span>
          )}
          {item.is_signed && (
            <span className="px-1.5 py-0.5 bg-purple-500/90 text-white text-[10px] font-bold rounded">
              Signed
            </span>
          )}
          {item.is_for_sale && (
            <span className="px-1.5 py-0.5 bg-orange-500/90 text-white text-[10px] font-bold rounded">
              $
            </span>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="p-2">
        <h3 className="font-medium text-white text-sm truncate">
          {item.title || 'Untitled'}
        </h3>
        <p className="text-xs text-zinc-400">
          #{item.issue_number || '?'}
          {item.year && ` (${item.year})`}
        </p>

        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs font-medium ${gradeColor}`}>
            {item.is_graded
              ? `${item.grading_company?.toUpperCase()} ${item.user_grade}`
              : item.user_grade || 'Raw'}
          </span>
          <AutoGradeStatusBadge status={item.auto_grade_status} gradeValue={item.auto_grade_value} />
        </div>
      </div>
    </article>
  );
}

// List view row with ARIA
function VaultItemRow({ item, isSelected, isActive, onClick, onSelect, style }) {
  return (
    <div
      role="listitem"
      aria-selected={isActive}
      aria-label={`${item.title} Issue ${item.issue_number}`}
      tabIndex={0}
      style={style}
      onClick={() => onClick(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(item);
        }
      }}
      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
        isActive
          ? 'bg-orange-500/20 border border-orange-500/50'
          : isSelected
          ? 'bg-zinc-800/50 border border-orange-500/30'
          : 'hover:bg-zinc-800 border border-transparent'
      }`}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(item);
        }}
        aria-label={isSelected ? 'Deselect item' : 'Select item'}
        aria-pressed={isSelected}
        className="p-1"
      >
        {isSelected ? (
          <CheckSquare className="w-4 h-4 text-orange-400" aria-hidden="true" />
        ) : (
          <Square className="w-4 h-4 text-zinc-500" aria-hidden="true" />
        )}
      </button>

      <div className="w-10 h-14 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
        {item.cover_photo_url ? (
          <img src={item.cover_photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
            <Package className="w-5 h-5 text-zinc-700" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-white text-sm truncate">{item.title}</p>
        <p className="text-xs text-zinc-400">
          #{item.issue_number} {item.publisher && `- ${item.publisher}`}
        </p>
      </div>

      <div className="flex items-center gap-3">
        {item.is_for_sale && (
          <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">
            For Sale
          </span>
        )}
        <span className={`text-xs font-medium ${item.is_graded ? 'text-green-400' : 'text-zinc-400'}`}>
          {item.is_graded
            ? `${item.grading_company?.toUpperCase()} ${item.user_grade}`
            : item.user_grade || 'Raw'}
        </span>
      </div>
    </div>
  );
}

// Virtualized row renderer for react-window
const VirtualRow = ({ data, index, style }) => {
  const { items, selectedItems, activeItem, onItemClick, handleToggleSelect } = data;
  const item = items[index];

  return (
    <VaultItemRow
      item={item}
      isSelected={selectedItems.includes(item.id)}
      isActive={activeItem?.id === item.id}
      onClick={onItemClick}
      onSelect={handleToggleSelect}
      style={style}
    />
  );
};

export default function VaultGrid({
  items,
  isLoading,
  activeItem,
  activeIndex = -1,
  selectedItems,
  onItemClick,
  onItemDoubleClick,
  onSelectionChange,
  viewMode,
  onViewModeChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  onRefresh,
  onBulkAction,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  searchInputRef,
}) {
  const gridRef = useRef(null);
  const listRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(500);

  // Measure container for virtualization
  useEffect(() => {
    const updateHeight = () => {
      if (gridRef.current) {
        setContainerHeight(gridRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Scroll active item into view for virtualized list
  useEffect(() => {
    if (viewMode === 'list' && listRef.current && activeIndex >= 0) {
      listRef.current.scrollToItem(activeIndex, 'smart');
    }
  }, [activeIndex, viewMode]);

  // Filter items client-side for search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const search = searchQuery.toLowerCase();
    return items.filter((item) =>
      (item.title?.toLowerCase().includes(search)) ||
      (item.issue_number?.toLowerCase().includes(search)) ||
      (item.publisher?.toLowerCase().includes(search))
    );
  }, [items, searchQuery]);

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(filteredItems.map(i => i.id));
    }
  };

  const handleToggleSelect = useCallback((item) => {
    if (selectedItems.includes(item.id)) {
      onSelectionChange(selectedItems.filter(id => id !== item.id));
    } else {
      onSelectionChange([...selectedItems, item.id]);
    }
  }, [selectedItems, onSelectionChange]);

  // List data for virtualization
  const listData = useMemo(() => ({
    items: filteredItems,
    selectedItems,
    activeItem,
    onItemClick,
    handleToggleSelect,
  }), [filteredItems, selectedItems, activeItem, onItemClick, handleToggleSelect]);

  return (
    <div
      className="flex-1 flex flex-col min-w-0 bg-zinc-950"
      role="region"
      aria-label="Collection items"
    >
      {/* Toolbar */}
      <div className="p-4 border-b border-zinc-800 flex flex-wrap items-center gap-3" role="toolbar" aria-label="Collection toolbar">
        {/* Search */}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search collection... (Press /)"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search collection"
            className="w-full pl-9 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:border-orange-500 focus:outline-none text-sm"
          />
        </div>

        {/* Sort */}
        <label className="sr-only" htmlFor="sort-select">Sort by</label>
        <select
          id="sort-select"
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          aria-label="Sort collection"
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm"
        >
          <option value="created_at:desc">Newest First</option>
          <option value="created_at:asc">Oldest First</option>
          <option value="title:asc">Title A-Z</option>
          <option value="title:desc">Title Z-A</option>
          <option value="user_grade:desc">Grade High-Low</option>
          <option value="market_value:desc">Value High-Low</option>
        </select>

        {/* View Toggle */}
        <div className="flex bg-zinc-900 rounded-lg p-0.5 border border-zinc-800" role="tablist" aria-label="View mode">
          <button
            role="tab"
            aria-selected={viewMode === 'grid'}
            aria-label="Grid view"
            onClick={() => onViewModeChange('grid')}
            className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
          >
            <Grid className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'list'}
            aria-label="List view"
            onClick={() => onViewModeChange('list')}
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
          >
            <List className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Refresh */}
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          aria-label="Refresh collection"
        >
          <RefreshCw className={`w-4 h-4 text-zinc-400 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {/* Selection Bar */}
      {selectedItems.length > 0 && (
        <div
          className="px-4 py-2 bg-orange-500/10 border-b border-orange-500/20 flex items-center justify-between"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={handleSelectAll}
              className="text-sm text-orange-400 hover:text-orange-300"
            >
              {selectedItems.length === filteredItems.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-sm text-zinc-400">
              {selectedItems.length} selected
            </span>
          </div>
          <div className="flex items-center gap-2" role="toolbar" aria-label="Bulk actions">
            <button
              onClick={() => onBulkAction?.('sell-queue', selectedItems)}
              className="flex items-center gap-1 px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded text-sm"
              aria-label="Add selected to sell queue"
            >
              <DollarSign className="w-3 h-3" aria-hidden="true" />
              Sell Queue
            </button>
            <button
              onClick={() => onBulkAction?.('assign-box', selectedItems)}
              className="flex items-center gap-1 px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded text-sm"
              aria-label="Assign selected to box"
            >
              <Box className="w-3 h-3" aria-hidden="true" />
              Assign Box
            </button>
            <button
              onClick={() => onBulkAction?.('delete', selectedItems)}
              className="flex items-center gap-1 px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm"
              aria-label="Delete selected items"
            >
              <Trash2 className="w-3 h-3" aria-hidden="true" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div ref={gridRef} className="flex-1 overflow-y-auto p-4" role="list" aria-label="Collection items">
        {isLoading ? (
          <div className="flex items-center justify-center py-16" role="status" aria-label="Loading">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="sr-only">Loading collection...</span>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16" role="status">
            <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" aria-hidden="true" />
            <p className="text-zinc-500 text-sm">
              {searchQuery ? 'No items match your search' : 'No items in this view'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3"
            role="list"
          >
            {filteredItems.map((item, index) => (
              <VaultItemCard
                key={item.id}
                item={item}
                index={index}
                isSelected={selectedItems.includes(item.id)}
                isActive={activeItem?.id === item.id}
                onClick={onItemClick}
                onSelect={handleToggleSelect}
                onDoubleClick={onItemDoubleClick}
              />
            ))}
          </div>
        ) : (
          // Virtualized list for performance with large collections
          <VirtualList
            ref={listRef}
            height={containerHeight - 32} // Subtract padding
            width="100%"
            itemCount={filteredItems.length}
            itemSize={60} // Row height
            itemData={listData}
            overscanCount={5}
          >
            {VirtualRow}
          </VirtualList>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="p-4 border-t border-zinc-800 flex items-center justify-between" aria-label="Pagination">
          <span className="text-sm text-zinc-500">
            {totalCount} items
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label="Previous page"
              className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm"
            >
              Prev
            </button>
            <span className="text-sm text-zinc-400" aria-current="page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
              className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm"
            >
              Next
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
